const nonBreakable = (s: string) => s.replace(' ', ' ');

/*

Metadata about mitigation states. In the long run this should live on the backend :/

Active/Clear/Failed:
- active: is this mitigation effective?
- clear: is this mitigation done?
- failed: did this fail somehow (eg. can't apply on remote mit platform)
- If active and clear are both false, the mitigation is not yet active nor done (eg. start wait states)

Notify: should we email about this state?

Version: just for us to help keep track of deprecated/old stuff.

Rank: used for sorting; organized in rough tiers:
    10: all failures
    20: manual states
    30: "hot" auto states
    40: alarms
    50: "cold" auto states
    70: default
    90: deprecated

Label: UI description

*/

const stateDesc = Object.entries({
  // mit 2.1 + 2.0
  START_WAIT: { active: 0, clear: 0, failed: 0, notify: 0, version: 2.1, rank: 50, label: nonBreakable('Start Wait') },
  STARTING: { active: 0, clear: 0, failed: 0, notify: 0, version: 2.1, rank: 30, label: nonBreakable('Starting') },
  STARTING_FAIL: {
    active: 0,
    clear: 0,
    failed: 1,
    notify: 1,
    version: 2.1,
    rank: 10,
    label: nonBreakable('Failed to start')
  },
  MITIGATING: { active: 1, clear: 0, failed: 0, notify: 1, version: 2.1, rank: 30, label: nonBreakable('Active') },
  END_GRACE: { active: 1, clear: 0, failed: 0, notify: 0, version: 2.1, rank: 50, label: nonBreakable('End Grace') },
  END_WAIT: { active: 1, clear: 0, failed: 0, notify: 0, version: 2.1, rank: 50, label: nonBreakable('End Wait') },
  CLEARING: { active: 1, clear: 0, failed: 0, notify: 0, version: 2.1, rank: 50, label: nonBreakable('Clearing') },
  CLEARING_FAIL: {
    active: 1,
    clear: 0,
    failed: 1,
    notify: 1,
    version: 2.1,
    rank: 10,
    label: nonBreakable('Failed to clear')
  },
  ACK_REQ: {
    active: 0,
    clear: 1,
    failed: 0,
    notify: 1,
    version: 2.1,
    rank: 50,
    label: nonBreakable('Acknowledgement Required')
  },
  ARCHIVED: { active: 0, clear: 1, failed: 0, notify: 0, version: 2.1, rank: 70, label: nonBreakable('Archived') },
  MANUAL_STARTING: {
    active: 0,
    clear: 0,
    failed: 0,
    notify: 0,
    version: 2.1,
    rank: 20,
    label: nonBreakable('Manual: Starting')
  },
  MANUAL_STARTING_FAIL: {
    active: 0,
    clear: 0,
    failed: 1,
    notify: 1,
    version: 2.1,
    rank: 10,
    label: nonBreakable('Manual: Failed to start')
  },
  MANUAL_MITIGATING: {
    active: 1,
    clear: 0,
    failed: 0,
    notify: 1,
    version: 2.1,
    rank: 20,
    label: nonBreakable('Manual: Active')
  },
  MANUAL_CLEARING: {
    active: 1,
    clear: 0,
    failed: 0,
    notify: 0,
    version: 2.1,
    rank: 20,
    label: nonBreakable('Manual: Clearing')
  },
  MANUAL_CLEARING_FAIL: {
    active: 1,
    clear: 0,
    failed: 1,
    notify: 1,
    version: 2.1,
    rank: 10,
    label: nonBreakable('Manual: Failed to clear')
  },
  MANUAL_CLEAR: {
    active: 0,
    clear: 1,
    failed: 0,
    notify: 1,
    version: 2.1,
    rank: 20,
    label: nonBreakable('Manual: Clear')
  },

  // mit 2.0 only
  MITIGATING_HOLD: { active: 1, clear: 0, failed: 0, notify: 0, version: 2.0, rank: 20, label: nonBreakable('Hold') },
  CLEARING_HOLD: { active: 1, clear: 0, failed: 0, notify: 0, version: 2.0, rank: 20, label: nonBreakable('Clearing') },
  CLEAR_HOLD: {
    active: 0,
    clear: 1,
    failed: 0,
    notify: 0,
    version: 2.0,
    rank: 20,
    label: nonBreakable('Manual Clear')
  },
  CLEAR: { active: 0, clear: 1, failed: 0, notify: 1, version: 2.0, rank: 70, label: nonBreakable('Cleared') },

  // not actual mitigation states, but necessary for sorting
  NONE: { active: 0, clear: 0, failed: 0, notify: 0, version: 0.0, rank: 70, label: nonBreakable('None') },
  ALARM: { active: 0, clear: 0, failed: 0, notify: 0, version: 0.0, rank: 40, label: nonBreakable('Alarm') }

  // unused mit 2.0
  // PREEMPTING:           {active: 1, clear: 0, failed: 0, notify: 0, version: 2.0, rank: 90, label: nonBreakable('Preempting')},
  // PREEMPTING_FAIL:      {active: 1, clear: 0, failed: 1, notify: 1, version: 2.0, rank: 90, label: nonBreakable('Failed to preempt')},
  // PREEMPTED:            {active: 0, clear: 1, failed: 0, notify: 0, version: 2.0, rank: 90, label: nonBreakable('Preempted')},

  // deprecated mit 1.0
  // CLEAR_MANUAL:         {active: 0, clear: 0, failed: 0, notify: 0, version: 1.0, rank: 90, label: nonBreakable('Manual Clear')},
  // END_TIMED_CONF:       {active: 0, clear: 0, failed: 0, notify: 0, version: 1.0, rank: 90, label: nonBreakable('End Time Conf')},
  // END_WAIT_CONF:        {active: 0, clear: 0, failed: 0, notify: 0, version: 1.0, rank: 90, label: nonBreakable('End Wait Conf')},
  // MITIGATING_MANUAL:    {active: 0, clear: 0, failed: 0, notify: 0, version: 1.0, rank: 90, label: nonBreakable('Manual')},
  // NEW:                  {active: 0, clear: 0, failed: 0, notify: 0, version: 1.0, rank: 90, label: nonBreakable('Initializing')},
  // START_TIMED_CONF:     {active: 0, clear: 0, failed: 0, notify: 0, version: 1.0, rank: 90, label: nonBreakable('Start Timed Conf')},
  // START_WAIT_CONF:      {active: 0, clear: 0, failed: 0, notify: 0, version: 1.0, rank: 90, label: nonBreakable('Start Wait Conf')},
});

export = {
  validMitigationStatesForEmail: stateDesc.filter((e) => e[1].notify === 1).map((e) => e[0]),
  activeStates: stateDesc.filter((e) => e[1].active === 1).map((e) => e[0]),
  clearStates: stateDesc.filter((e) => e[1].clear === 1).map((e) => e[0]),
  alarmAndMitigationStateSortOrder: stateDesc
    .map((e) => [e[1].rank, e[0]])
    .sort()
    .map((e) => e[1]),
  querySort: stateDesc
    .map((e) => [e[1].rank, e[0]])
    .sort()
    .map((e) => e[1]), // FIXME: same as alarmAndMitigationStateSortOrder, dedupe?
  stateLabels: stateDesc.reduce(
    (map, obj) => {
      map[obj[0]] = obj[1].label;
      return map;
    },
    {} as Record<string, string>
  )
};
